// -----------------------------------------------------------------------------
// tocNumbering.js — shared helper used by ManualCompose, ChapterCard and
// BookPreviewPane to derive display labels for each chapter row.
//
// Data model (iter-36):
//   Each chapter row may declare a ``kind`` of "chapter" (default), "part" or
//   "section", plus an optional ``label`` string that overrides the auto
//   label.
//     - kind="chapter"  → numbered continuously across the book. Auto label
//                         "Chapter 1", "Chapter 2", … Numbering never
//                         resets inside a Part (mirrors the user's real
//                         TOC: Chapters 1-5 in Part I, then 6-10 in Part II).
//     - kind="part"     → not numbered, styled as a divider. Auto label
//                         "Part I", "Part II", … using Roman numerals.
//     - kind="section"  → not numbered, rendered as a titled entry in the
//                         TOC + body (great for mid-book "Resources" /
//                         "Interlude" / "Bonus" pages).
//
//   ``label`` when non-empty always wins over the auto label — lets an
//   author write "Bonus Chapter" or "Interlude I" without fighting the
//   numbering engine.
// -----------------------------------------------------------------------------

/** Integer → Roman numeral. 1 → "I", 4 → "IV", 9 → "IX", 40 → "XL", ... */
export function toRoman(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  const pairs = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
    [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
    [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
  ];
  let out = "";
  let v = Math.floor(n);
  for (const [val, sym] of pairs) {
    while (v >= val) { out += sym; v -= val; }
  }
  return out;
}

/** Integer → spelled-out English word: 1 → "One", 21 → "Twenty-One".
 *  Bounded to 1..99 which covers every real book we've seen; anything
 *  above 99 falls back to Arabic to keep labels sane. */
const _ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
                "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen",
                "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen",
                "Nineteen"];
const _TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty",
                "Seventy", "Eighty", "Ninety"];
export function toWords(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  n = Math.floor(n);
  if (n > 99) return String(n);
  if (n < 20) return _ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? _TENS[t] : `${_TENS[t]}-${_ONES[o]}`;
}

/** Format a chapter number using the book-level numbering style.
 *  "arabic" (default) | "roman" | "words" | "none" — Parts are
 *  intentionally always Roman regardless of this setting. */
export function formatChapterNumber(n, style = "arabic") {
  if (!Number.isFinite(n) || n <= 0) return "";
  switch ((style || "").toLowerCase()) {
    case "roman": return toRoman(n);
    case "words": return toWords(n);
    case "none":  return "";
    case "arabic":
    default:      return String(n);
  }
}

/** Detect a row's effective kind. Falls back to "part" when the title
 *  looks like "Part I / Part 2 / PART III" so DOCX-uploaded books and
 *  older manual books auto-migrate on the first render. */
export function inferKind(row) {
  const k = (row?.kind || "").toLowerCase();
  if (k === "part" || k === "section" || k === "chapter" || k === "appendix" || k === "preface") return k;
  const title = (row?.title || "").trim();
  if (/^part\s+([ivxlcdm]+|\d+)\b/i.test(title)) return "part";
  return "chapter";
}

/** Compute per-row TOC/body labels for the whole chapter list.
 *  Returns an array with one entry per input row:
 *    { kind, autoLabel, label, tocNumber, chapterNumber, partNumber }
 *  - autoLabel  — the generated "Chapter 3" / "Part II" / "" string.
 *  - label      — the row's explicit override if provided, else autoLabel.
 *  - tocNumber  — 1-based index within the TOC (only set for kind=chapter).
 *  - chapterNumber / partNumber — 1-based counter across the whole book.
 *
 *  ``style`` — one of "arabic" (default) / "roman" / "words" / "none"
 *  controls how chapter counters are rendered. Parts always use Roman
 *  regardless of the setting. "none" produces a bare "Chapter" label. */
export function computeChapterLabels(rows, style = "arabic") {
  const out = [];
  let chapterCounter = 0;
  let partCounter = 0;
  for (const row of rows || []) {
    const kind = inferKind(row);
    let autoLabel = "";
    let chapterNumber = null;
    let partNumber = null;
    if (kind === "chapter") {
      chapterCounter += 1;
      chapterNumber = chapterCounter;
      const rendered = formatChapterNumber(chapterCounter, style);
      autoLabel = rendered ? `Chapter ${rendered}` : "Chapter";
    } else if (kind === "part") {
      partCounter += 1;
      partNumber = partCounter;
      autoLabel = `Part ${toRoman(partCounter)}`;
    }
    const label = (row?.label || "").trim() || autoLabel;
    out.push({
      kind,
      autoLabel,
      label,
      tocNumber: kind === "chapter" ? chapterCounter : null,
      chapterNumber, partNumber,
    });
  }
  return out;
}
